import { React, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, FormHelperText, Typography, InputAdornment } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import * as Yup from "yup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import CustomInput from "../CustomInput/CustomInput";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { setOpenLoginFlow, setOpenSignupFlow, setOpenAgentSignupFlow, setAgentSignupStepCount } from "../../redux/login";
import { LoginUser, SignUpUser, socialLoginService } from "../../services/LoginUser";
import Loader from "../loader/Loader";
import { ReactComponent as EyeIcon } from '../../assets/svg/visible.svg';
import { ReactComponent as OffEyeIcon } from '../../assets/svg/invisible.svg';
import { ReactComponent as GoogleIcon } from '../../assets/svg/google-icon.svg';

const SignUp = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('password');
  const [confirmType, setConfirmType] = useState('password');
  const [password, setPassword] = useState("");
  const handleLogin = async (values, { resetForm }) => {
    try {
      let formData = new FormData();
      const fullname = [values.first_name, values.middle_name, values.last_name]
        .filter(Boolean)
        .join(" ");
      formData.append("fullname", fullname);
      formData.append("email", values.email);
      formData.append("contact_number", values.contact_number);
      formData.append("password", values.password);
      formData.append("user_type", values.user_type);

      setLoading(true);
      const response = await SignUpUser(formData, {
        headers: { "content-type": "multipart/form-data" },
      });

      if (response) {
        const loginFormData = new FormData();
        loginFormData.append("email", values.email);
        loginFormData.append("password", values.password);
        loginFormData.append("remember_token", 1);

        const loginResponse = await dispatch(
          LoginUser(loginFormData, {
            headers: { "content-type": "multipart/form-data" },
            navigate,
          }),
        );

        if (!loginResponse) {
          setLoading(false);
          dispatch(setOpenSignupFlow(false));
          dispatch(setOpenLoginFlow(true));
          return;
        }

        if (loginResponse.emailVerificationPending) {
          dispatch(setOpenSignupFlow(false));
          dispatch(setAgentSignupStepCount(4));
          dispatch(setOpenAgentSignupFlow(true));
          setLoading(false);
          return;
        }

        if (onClose) {
          onClose();
        }
        dispatch(setOpenSignupFlow(false));
        dispatch(setOpenLoginFlow(false));

        resetForm();
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };
  const handleToggle = () => {
    if (type === 'password') {
      setType('text');
    } else {
      setType('password');
    }
  };

  const handleConfirmToggle = () => {
    if (confirmType === 'password') {
      setConfirmType('text');
    } else {
      setConfirmType('password');
    }
  };

  const handleGoogleSignIn = () => {
    try {
      dispatch(
        socialLoginService({
          params: {
            userType: 1,
          },
        }),
      );
    } catch (error) {
      console.error("Google sign-in error:", error);
    }
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '' };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    const label = score <= 1 ? 'Weak' : score === 2 ? 'Fair' : score === 3 ? 'Good' : 'Strong';
    return { score, label };
  };
  const formik = useFormik({
    initialValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      contact_number: "+258 8",
      password: "",
      confirm_password: "",
      user_type: 1,
      tnc: false,
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required("First name is required")
        .matches("^[a-zA-Z'\. ]*$", "First name must be valid"),
      middle_name: Yup.string()
        .notRequired()
        .matches("^[a-zA-Z'\. ]*$", "Middle name must be valid", { excludeEmptyString: true }),
      last_name: Yup.string()
        .required("Last name is required")
        .matches("^[a-zA-Z'\. ]*$", "Last name must be valid"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      contact_number: Yup.string()
        .required("Mobile Number is required")
        .test(
          "len",
          "Phone number must be exactly 8 digits",
          (val) => val?.replace(/^\+258 8/, "").length === 8
        ),
      password: Yup.string()
        .required(t("Validation.New password is required!"))
        .min(8, t("Validation.Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"))
        .max(128, t("Validation.Password cannot exceed 128 characters")),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
      tnc: Yup.boolean().oneOf([true], "You must accept the terms"),
    }),
    onSubmit: handleLogin,
  });

  return (
    <>
      {loading && <Loader />}

      <form onSubmit={formik.handleSubmit} noValidate>
        {/* <Box className="signup-form"> */}
        <Box className="signup-form-inner">

          <CustomInput
            fullWidth
            label={t("CustomerRegistration.FirstName") || "First name"}
            name="first_name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            error={formik.touched.first_name && Boolean(formik.errors.first_name)}
            helperText={formik.touched.first_name && formik.errors.first_name}
          />

          <CustomInput
            fullWidth
            label={t("CustomerRegistration.MiddleName") || "Middle name"}
            name="middle_name"
            value={formik.values.middle_name}
            onChange={formik.handleChange}
            error={formik.touched.middle_name && Boolean(formik.errors.middle_name)}
            helperText={formik.touched.middle_name && formik.errors.middle_name}
          />

          <CustomInput
            fullWidth
            label={t("CustomerRegistration.LastName") || "Last name"}
            name="last_name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
            helperText={formik.touched.last_name && formik.errors.last_name}
          />

          <CustomInput
            fullWidth
            label={t("CustomerRegistration.ContactNo")}
            name="contact_number"
            value={formik.values.contact_number}
            onKeyDown={(e) => {
              const PREFIX = "+258 8";
              if (
                (e.key === "Backspace" || e.key === "Delete") &&
                e.target.selectionStart <= PREFIX.length
              ) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const PREFIX = "+258 8";
              const value = e.target.value;
              if (!value.startsWith(PREFIX)) {
                formik.setFieldValue("contact_number", PREFIX);
                return;
              }
              let numericSuffix = value.slice(PREFIX.length).replace(/\D/g, "");
              if (numericSuffix.length > 8) numericSuffix = numericSuffix.slice(0, 8);
              formik.setFieldValue("contact_number", PREFIX + numericSuffix);
            }}
            error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
            helperText={formik.touched.contact_number && formik.errors.contact_number}
          />

          <CustomInput
            fullWidth
            label={t("LogIn.EmailAddress")}
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <CustomInput
            fullWidth
            type={type}
            label={t("CustomerRegistration.YourPwd")}
            name="password"
            value={formik.values.password}
            onChange={(e) => { formik.handleChange(e); setPassword(e.target.value); }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" onClick={handleToggle} sx={{ width: '25px', height: "25px", cursor: 'pointer' }}>
                  {type == 'password' && <OffEyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                  {type == 'text' && <EyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                </InputAdornment>
              ),
            }}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />

          {password ? (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ height: 6, background: '#E6E6E6', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%',
                  width: `${(getPasswordStrength(password).score / 4) * 100}%`,
                  background: getPasswordStrength(password).score <= 1 ? '#ff4d4f' : getPasswordStrength(password).score === 2 ? '#faad14' : getPasswordStrength(password).score === 3 ? '#52c41a' : '#237804',
                  borderRadius: 2,
                  transition: 'width .2s ease'
                }} />
              </Box>
              <Typography variant="xxsSize" className="font-w-400 gray-color" sx={{ mt: 0.5 }}>{getPasswordStrength(password).label}</Typography>
            </Box>
          ) : null}

          <CustomInput
            fullWidth
            type={confirmType}
            label={t("CustomerRegistration.ConfirmPwd")}
            name="confirm_password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" onClick={handleConfirmToggle} sx={{ width: '25px', height: "25px", cursor: 'pointer' }}>
                  {confirmType == 'password' && <OffEyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                  {confirmType == 'text' && <EyeIcon style={{ fill: '#0085FD', height: '26px', width: '26px' }} />}
                </InputAdornment>
              ),
            }}
            error={
              formik.touched.confirm_password &&
              Boolean(formik.errors.confirm_password)
            }
            helperText={
              formik.touched.confirm_password &&
              formik.errors.confirm_password
            }
          />

          <FormGroup className="agreement">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.tnc}
                  onChange={formik.handleChange}
                  name="tnc"
                />
              }
              label={t("CustomerRegistration.AgreementText")}
            />
            {formik.touched.tnc && Boolean(formik.errors.tnc) && (
              <FormHelperText error>
                {formik.errors.tnc}
              </FormHelperText>
            )}
          </FormGroup>

          <Button
            type="submit"
            fullWidth
            className="signup-primary-btn text-white font-w-400 mb-2"

          >
            {t("LogIn.SignUp")}
          </Button>

          <Typography className="account-text">
            {t("CustomerRegistration.HaveAnAccount")}{" "}
            <Link
              to="/"
              onClick={() => {
                dispatch(setOpenLoginFlow(true));
                dispatch(setOpenSignupFlow(false));
              }}
            >
              <span>{t("CustomerRegistration.HaveAnAccountLogin")}</span>
            </Link>
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#D9D9D9" }} />
            <Typography sx={{ margin: "0 12px", color: "#6B7280" }} variant="xsSize">
              {t("LogIn.OR") || "OR"}
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#D9D9D9" }} />
          </Box>

          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleSignIn}
            sx={{
              borderRadius: "12px",
              padding: "12px 16px",
              borderColor: "#D0D5DD",
              color: "#111111",
              textTransform: "none",
              fontWeight: 600,
              display: "flex",
              gap: 1.5,
              "&:hover": {
                borderColor: "#0085FD",
                backgroundColor: "#F8FBFF",
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "1px solid #D0D5DD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GoogleIcon width={16} height={16} />
            </Box>
            <Typography
              variant="xxsSize"
              component="span"
              sx={{ color: "#111111", fontWeight: 500 }}
            >
              Continue with Google
            </Typography>
          </Button>

        </Box>
        {/* </Box> */}
      </form>
    </>
  );
};

export default SignUp;
